import { Vector3 } from 'three';
import { Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';
import { buildCotangentLaplacian } from './CotangentLaplacian';
import { LinearSolver } from './LinearSolver';
import ArapCore, { ArapProblem, ArapSolveOptions } from './ArapCore';

export type { ArapSolveOptions } from './ArapCore';

export interface ArapBindOptions {
  /** Tikhonov regularization added to the free diagonal (relative to mean |diag|). */
  regularization?: number;
}

/**
 * As-Rigid-As-Possible (ARAP) surface deformer (Sorkine & Alexa 2007) over a half-edge
 * mesh with Dirichlet constraints — the `Vertex`-aware adapter around {@link ArapCore}.
 *
 * Usage:
 *   1. `bind(mesh, restPositions, constrained)` — once, while topology is fixed.
 *   2. `solveFrame(constraints)` — per settle; imposes the constraint positions and
 *      relaxes the interior (runs the core in-process).
 *   3. `applyFreePositions(cb)` — read back the solved free-vertex positions.
 *
 * For off-main-thread solving, the same problem can be sent to a worker: see
 * {@link getProblem}, {@link packConstraints} and {@link freeVertices}.
 */
export default class ArapDeformer {
  private order: Vertex[] = [];
  private indexOf: Map<Vertex, number> = new Map();
  private constrainedMask: Uint8Array = new Uint8Array(0);
  private constrainedSlot: Int32Array = new Int32Array(0); // global -> constrained slot | -1
  private restPos: Float64Array = new Float64Array(0);
  private problem: ArapProblem | null = null;
  private constrainedScratch: Float64Array = new Float64Array(0);

  private readonly core: ArapCore;

  constructor(solver?: LinearSolver) {
    this.core = new ArapCore(solver);
  }

  public get useRotations(): boolean {
    return this.core.useRotations;
  }
  public set useRotations(v: boolean) {
    this.core.useRotations = v;
  }
  public get isBound(): boolean {
    return this.core.isBound;
  }
  public get freeCount(): number {
    return this.core.freeList.length;
  }

  /** Builds topology, partitions free/constrained, and prefactors `L_ff`. */
  public bind(
    mesh: HalfEdgeMesh,
    restPositions: Map<Vertex, Vector3>,
    constrained: Iterable<Vertex>,
    options: ArapBindOptions = {},
  ): void {
    const topo = buildCotangentLaplacian(mesh, restPositions);
    this.order = topo.order;
    this.indexOf = topo.indexOf;
    this.restPos = topo.restPos;
    const n = topo.n;

    const mask = new Uint8Array(n);
    for (const v of constrained) {
      const i = topo.indexOf.get(v);
      if (i !== undefined) mask[i] = 1;
    }
    this.constrainedMask = mask;

    this.problem = {
      n,
      rowPtr: topo.rowPtr,
      colIdx: topo.colIdx,
      weight: topo.weight,
      diag: topo.diag,
      restPos: topo.restPos,
      constrainedMask: mask,
      regularization: options.regularization,
      useRotations: this.core.useRotations,
    };
    this.core.bind(this.problem);

    // Reverse map global -> constrained slot, for packing worker constraint arrays.
    this.constrainedSlot = new Int32Array(n).fill(-1);
    const cl = this.core.constrainedIndices;
    for (let c = 0; c < cl.length; c++) this.constrainedSlot[cl[c]] = c;
    this.constrainedScratch = new Float64Array(3 * cl.length);
  }

  /** The serializable problem (send to a worker once at bind). */
  public getProblem(): ArapProblem | null {
    return this.problem;
  }

  /** Free-interior vertices in the order the core/worker returns their positions. */
  public freeVertices(): Vertex[] {
    const fl = this.core.freeList;
    const out: Vertex[] = new Array(fl.length);
    for (let fi = 0; fi < fl.length; fi++) out[fi] = this.order[fl[fi]];
    return out;
  }

  /**
   * Packs constraint targets into a compact `3·nConstrained` array (constrained-index
   * order) to post to a worker. Constrained vertices missing from `constraints` default
   * to their rest position.
   */
  public packConstraints(constraints: Map<Vertex, Vector3>): Float64Array {
    const cl = this.core.constrainedIndices;
    const out = this.constrainedScratch;
    for (let c = 0; c < cl.length; c++) {
      const gi = cl[c];
      out[3 * c] = this.restPos[3 * gi];
      out[3 * c + 1] = this.restPos[3 * gi + 1];
      out[3 * c + 2] = this.restPos[3 * gi + 2];
    }
    for (const [v, p] of constraints) {
      const i = this.indexOf.get(v);
      if (i === undefined) continue;
      const slot = this.constrainedSlot[i];
      if (slot < 0) continue;
      out[3 * slot] = p.x;
      out[3 * slot + 1] = p.y;
      out[3 * slot + 2] = p.z;
    }
    return out;
  }

  /** Resets the free interior warm-start to the rest pose. */
  public resetWarmStart(): void {
    this.core.resetWarmStart();
  }

  /** Imposes `constraints` and relaxes the interior, running the core in-process. */
  public solveFrame(constraints: Map<Vertex, Vector3>, options: ArapSolveOptions = {}): void {
    const pos = this.core.pos;
    for (const [v, p] of constraints) {
      const i = this.indexOf.get(v);
      if (i === undefined || this.constrainedMask[i] === 0) continue;
      pos[3 * i] = p.x;
      pos[3 * i + 1] = p.y;
      pos[3 * i + 2] = p.z;
    }
    this.core.solve(options);
  }

  /** Calls `apply` for each free vertex with its solved mesh-local position (in-process). */
  public applyFreePositions(apply: (vertex: Vertex, x: number, y: number, z: number) => void): void {
    const fl = this.core.freeList;
    const pos = this.core.pos;
    for (let fi = 0; fi < fl.length; fi++) {
      const gi = fl[fi];
      apply(this.order[gi], pos[3 * gi], pos[3 * gi + 1], pos[3 * gi + 2]);
    }
  }

  /** Applies free positions returned from a worker (compact `3·nFree`, free-list order). */
  public applyFreePositionsFromArray(
    freeValues: Float64Array,
    apply: (vertex: Vertex, x: number, y: number, z: number) => void,
  ): void {
    const fl = this.core.freeList;
    for (let fi = 0; fi < fl.length; fi++) {
      apply(this.order[fl[fi]], freeValues[3 * fi], freeValues[3 * fi + 1], freeValues[3 * fi + 2]);
    }
  }

  /** Current deformed position of a vertex (for tests). Null if unbound/unknown. */
  public positionOf(vertex: Vertex): [number, number, number] | null {
    const i = this.indexOf.get(vertex);
    if (i === undefined) return null;
    const pos = this.core.pos;
    return [pos[3 * i], pos[3 * i + 1], pos[3 * i + 2]];
  }
}
